<template>
  <v-flex xs12 sm10 md8 lg6>
    <v-card class="py-4">
      <v-flex xs12 pb-3 px-4 v-if="err">
        <v-alert :value="true" type="error">{{ err }}</v-alert>
      </v-flex>
      <v-flex xs12 pb-3 px-4 v-if="success">
        <v-alert :value="true" type="success">{{ success }}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-layout row wrap justify-space-between align-center class="px-4">
          <v-flex xs12>
            <v-text-field
              :label="$lang.labels.corporationName"
              v-model="corporation.name"
              required
              :rules="[v => !!v || `${$lang.errors.missingCorporationName}`]"
            />
          </v-flex>
          <v-flex xs12>
            <v-autocomplete
              v-model="corporation.companies"
              :items="companies"
              :label="$lang.actions.selectCompanies"
              :rules="[() => corporation.companies.length !== 0 || `${$lang.errors.missingCompanies}`]"
              :loading="companyLoading"
              chips
              deletable-chips
              multiple
              item-text="name"
              item-value="id"
              @change="filterCompanies()"
            />
          </v-flex>
          <v-flex
            v-if="corporation.companies.length > 0"
            xs12>
            <v-autocomplete
              v-model="corporation.primaryCompanyId"
              :items="filteredCompanies"
              :label="$lang.labels.primaryCompany"
              :rules="[v => !!v || `${$lang.errors.primaryCompany}`]"
              required
              item-text="name"
              item-value="id"
            />
          </v-flex>
        </v-layout>
        <v-layout
          v-for="(setting, index) in settings"
          :key="index"
          align-center
          wrap
          pa-2
          row>
          <v-flex shrink px-3 style="min-width: 200px;">
            <v-checkbox
              v-model="corporation[setting].enabled"
              :label="$lang.labels[setting]"
              hide-details
              color="primary"
            />
          </v-flex>
          <v-flex grow px-3 pt-2>
            <v-text-field
              v-model="corporation[setting].serverUrl"
              :label="`${$lang.labels[setting]} URL`"
              :rules="[
                (corporation[setting].serverUrl && corporation[setting].enabled)
                || !corporation[setting].enabled
                || $lang.errors.serverUrl
              ]"
              type="text"
              color="primary"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between class="px-2 pb-4">
          <v-flex xs12 px-3 pt-2>
            <h3>{{ $lang.labels.dailyOrderCounterPrefixes }}</h3>
          </v-flex>
          <v-flex xs12 px-2>
            <v-layout
              align-center
              wrap
              pa-2
              row>
              <v-flex xs12 sm4 pr-1 pt-2>
                <v-text-field
                  v-model="corporation.dailyOrderCounterPrefixes.delivery"
                  :label="$lang.labels.delivery"
                  type="text"
                  color="primary"
                  :maxlength="dailyOrderCounterPrefixesMax"
                />
              </v-flex>
              <v-flex xs12 sm4 px-1 pt-2>
                <v-text-field
                  v-model="corporation.dailyOrderCounterPrefixes.collection"
                  :label="$lang.labels.collection"
                  type="text"
                  color="primary"
                  :maxlength="dailyOrderCounterPrefixesMax"
                />
              </v-flex>
              <v-flex xs12 sm4 pl-1 pt-2>
                <v-text-field
                  v-model="corporation.dailyOrderCounterPrefixes.takeaway"
                  :label="$lang.labels.takeaway"
                  type="text"
                  color="primary"
                  :maxlength="dailyOrderCounterPrefixesMax"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between class="px-2">
          <v-text-field
            v-if="isEdit && createdOn"
            :label="$lang.labels.created"
            v-model="createdOn"
            readonly
            class="min-w max-w mx-3"
          />
          <v-text-field
            v-if="isEdit && modifiedOn"
            :label="$lang.labels.modified"
            v-model="modifiedOn"
            readonly
            class="min-w max-w mx-3"
          />
        </v-layout>
        <v-layout row wrap justify-space-between align-content-center class="mt-3 px-4">
          <v-btn
            flat class="ml-0"
            @click="$router.push({ name: 'corporations' })">
            <v-icon>keyboard_arrow_left</v-icon>
            {{ $lang.actions.close }}
          </v-btn>
          <v-btn
            class="primary mx-0"
            :disabled="!valid"
            @click="submit">
            {{ isEdit ? $lang.actions.update : $lang.actions.create }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card>
  </v-flex>
</template>

<script>
import {DateTime} from 'luxon';

export default {
  data: () => ({
    settings: [
      'contactServerSettings',
      'addressServerSettings',
      'walletServerSettings',
      'depServerSettings',
      'inventoryServerSettings',
      'authServerSettings',
      'reservationServerSettings',
    ],
    err: '',
    success: '',
    valid: false,
    isEdit: false,
    companyLoading: true,
    companies: [],
    filteredCompanies: [],
    corporation: {
      companies: [],
      name: null,
      contactServerSettings: {
        enabled: false,
        serverUrl: '',
      },
      addressServerSettings: {
        enabled: false,
        serverUrl: '',
      },
      walletServerSettings: {
        enabled: false,
        serverUrl: '',
      },
      depServerSettings: {
        enabled: false,
        serverUrl: '',
      },
      inventoryServerSettings: {
        enabled: false,
        serverUrl: '',
      },
      authServerSettings:
        {
          enabled: false,
          serverUrl: ''
        },
      reservationServerSettings:
        {
          enabled: false,
          serverUrl: ''
        },
      primaryCompanyId: '',
      dailyOrderCounterPrefixes: {
        delivery: '',
        collection: '',
        takeaway: ''
      }
    },
    dailyOrderCounterPrefixesInit: ['delivery', 'collection', 'takeaway'],
    dailyOrderCounterPrefixesMax: 4,
    createdOn: '',
    modifiedOn: ''
  }),
  watch: {
    err(val) {
      if (val !== '') {
        setTimeout(() => {
          this.err = '';
        }, 5000);
      }
    },
  },
  methods: {
    filterCompanies() {
      const slecectedCompanies = this.corporation.companies;
      if (slecectedCompanies.length === 0) this.corporation.primaryCompanyId = '';
      if (slecectedCompanies.length > 0) {
        this.filteredCompanies = slecectedCompanies.map(id => {
          const found = this.companies.find(company => company.id === id);
          return {
            ...found,
          }
        });
        const isDefinded = this.filteredCompanies.find(
          company => company.id === this.corporation.primaryCompanyId);
        if (!isDefinded) {
          this.corporation.primaryCompanyId = '';
        }
      }
    },
    formatDate(date) {
      return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
    },
    init() {
      return new Promise((resolve) => {
        this.$http.get('company')
          .then((res) => {
            resolve()
            this.companyLoading = false;
            if (res.body.status === 'Success') {
              this.companies = res.body.companies;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            resolve()
            this.companyLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      });
    },
    submit() {
      let url = this.isEdit ? `corporation/update/${this.$route.params.id}` : 'corporation/create';
      this.dailyOrderCounterPrefixesInit.forEach(type => {
        if (this.corporation.dailyOrderCounterPrefixes[type] === '') delete this.corporation.dailyOrderCounterPrefixes[type];
      })
      this.$http.post(url, this.corporation)
        .then((res) => {
          if (res.body.status === 'Success') {
            this.success = this.isEdit ? 'Corporation updated successfully' : 'Corporation created successfully';
            setTimeout(() => {
              if (this.isEdit) {
                this.success = '';
              } else {
                this.$router.push({name: 'corporations'});
              }
            }, 3000);
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.err = err.body.statusText || err.body.message;
        });
    },
  },
  created() {
    this.init()
      .then(() => {
        if (this.$route.params.id) {
          this.isEdit = true;
          this.$http.get(`corporation/${this.$route.params.id}`)
            .then((res) => {
              if (res.body.status === 'Success') {
                this.corporation = res.body.corporation;
                this.filterCompanies();
                if (!this.corporation.dailyOrderCounterPrefixes) {
                  this.corporation.dailyOrderCounterPrefixes = {
                    delivery: '',
                    collection: '',
                    takeaway: ''
                  };
                } else {
                  this.dailyOrderCounterPrefixesInit.forEach(type => {
                    if (!this.corporation.dailyOrderCounterPrefixes[type]) this.corporation.dailyOrderCounterPrefixes[type] = '';
                  })
                }
                if (!this.corporation.authServerSettings) {
                  this.corporation.authServerSettings = {
                    enabled: false,
                    serverUrl: ''
                  };
                }
                if (!this.corporation.reservationServerSettings) {
                  this.corporation.reservationServerSettings = {
                    enabled: false,
                    serverUrl: ''
                  };
                }
                this.createdOn = this.formatDate(this.corporation.createdOn);
                if (this.corporation.modifiedOn) {
                  this.modifiedOn = this.formatDate(this.corporation.modifiedOn);
                }
                return;
              }
              this.err = res.body.statusText
            }, (err) => {
              this.err = err.body.statusText || err.body.message;
            })
        }
      })
  },
};
</script>



// WEBPACK FOOTER //
// CorporationDetails.vue?0c0b933a